import React, { useState } from 'react';
import { CheckCircle, Circle, AlertTriangle } from 'lucide-react';

const RoadmapSection = ({ isDarkMode }) => {
  const [expandedPhase, setExpandedPhase] = useState(1);

  const phases = [
    {
      id: 1,
      number: 1,
      title: 'Phase 1',
      badge: 'Current',
      circleColor: 'bg-green-500',
      lineColor: 'bg-green-400',
      content: {
        title: 'What We Track Right Now',
        intro: 'Right now, CivicDuty tracks the **Georgia State Legislature**:',
        items: [
          'All bills introduced and their current status',
          'Voting records for every state representative and senator',
          'Press releases from legislators'
        ],
        closing: 'We\'re currently in beta testing to refine and build out the application.'
      }
    },
    {
      id: 2,
      number: 2,
      title: 'Phase 2',
      badge: 'Georgia Expansion',
      circleColor: 'bg-[#3b82f6]',
      lineColor: 'bg-[#3b82f6]',
      content: {
        title: 'Expand Coverage Within Georgia',
        intro: 'After the legislative session, we plan to expand to cover every legislative body that voters are governed by:',
        items: [
          'Public Service Commission coverage',
          'County Commissions and Board of Education',
          'City Councils and Municipal Governments'
        ],
        closing: '**The goal:** If you live in Georgia, you should be able to see what\'s happening at the state, county, and city level—all in one place, all with the same ease.'
      }
    },
    {
      id: 3,
      number: 3,
      title: 'Phase 3',
      badge: 'Beyond Georgia',
      circleColor: 'bg-[#3b82f6]',
      lineColor: 'bg-gray-300',
      content: {
        title: 'Moving Beyond Georgia',
        intro: 'If we can make this work and prove it\'s usable for everyone, we hope to expand beyond Georgia:',
        items: [
          'Expansion to additional states',
          'Complete government transparency coverage',
          'Nationwide accessibility'
        ],
        closing: 'Our ultimate goal is to provide complete government transparency across the United States, making civic engagement accessible to everyone, everywhere.'
      }
    }
  ];

  const activePhaseData = phases.find(phase => phase.id === expandedPhase);

  return (
    <section className={`${isDarkMode ? 'bg-gray-900' : 'bg-white'} py-16 md:py-20`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <h2 className={`text-4xl md:text-5xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'} text-center mb-6`}>
          Plan and Roadmap
        </h2>

        {/* Disclaimer Box */}
        <div className={`${isDarkMode ? 'bg-yellow-900/20 border-yellow-800' : 'bg-yellow-50 border-yellow-200'} border rounded-lg p-4 mb-12 max-w-3xl mx-auto`}>
          <div className="flex items-center gap-3">
            <AlertTriangle className={`flex-shrink-0 w-5 h-5 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`} />
            <p className={`${isDarkMode ? 'text-gray-200' : 'text-gray-800'} text-center text-sm md:text-base flex-1`}>
              This roadmap is subject to change as we are still in very early beta.
            </p>
          </div>
        </div>

        {/* Horizontal Timeline */}
        <div className="flex flex-col md:flex-row items-center justify-center mb-8 gap-20 md:gap-48">
          {phases.map((phase, index) => {
            const isExpanded = expandedPhase === phase.id;
            
            return (
              <div key={phase.id} className="flex flex-col items-center">
                {/* Phase Circle Container */}
                <button
                  onClick={() => setExpandedPhase(isExpanded ? null : phase.id)}
                  className={`w-16 h-16 md:w-20 md:h-20 rounded-full border-2 border-white flex items-center justify-center text-white font-bold text-2xl md:text-3xl shadow-lg transition-all duration-200 hover:scale-110 ${
                    phase.circleColor
                  } ${isExpanded ? 'ring-4 ring-blue-200' : ''}`}
                >
                  {phase.number}
                </button>
                
                <div className="mt-3 text-center">
                  <div className={`text-lg md:text-xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'} mb-1`}>
                    {phase.title}
                  </div>
                  <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                    phase.id === 1 
                      ? isDarkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-700'
                      : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
                  }`}>
                    {phase.badge}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Expandable Content Block */}
        {expandedPhase && activePhaseData && (
          <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border p-8 md:p-10 shadow-md mt-8 animate-in slide-in-from-top-2 duration-300`}>
            <h3 className={`text-2xl md:text-3xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'} mb-6`}>
              {activePhaseData.content.title}
            </h3>
            
            {activePhaseData.id === 1 && (
              <>
                <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-6 leading-relaxed`}>
                  {activePhaseData.content.intro.split('**').map((part, index) => 
                    index % 2 === 1 ? (
                      <strong key={index}>{part}</strong>
                    ) : (
                      <span key={index}>{part}</span>
                    )
                  )}
                </p>
                <ul className="space-y-4 mb-6">
                  {activePhaseData.content.items.map((item, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-[#3b82f6] mr-3 flex-shrink-0 mt-0.5" />
                      <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} leading-relaxed`}>
                  {activePhaseData.content.closing}
                </p>
              </>
            )}
            
            {activePhaseData.id === 2 && (
              <>
                <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-6 leading-relaxed`}>
                  {activePhaseData.content.intro.split('**').map((part, index) => 
                    index % 2 === 1 ? (
                      <strong key={index}>{part}</strong>
                    ) : (
                      <span key={index}>{part}</span>
                    )
                  )}
                </p>
                <ul className="space-y-4 mb-6">
                  {activePhaseData.content.items.map((item, index) => (
                    <li key={index} className="flex items-start">
                      <Circle className={`w-5 h-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'} mr-3 flex-shrink-0 mt-0.5`} />
                      <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} leading-relaxed`}>
                  {activePhaseData.content.closing.split('**').map((part, index) => 
                    index % 2 === 1 ? (
                      <strong key={index}>{part}</strong>
                    ) : (
                      <span key={index}>{part}</span>
                    )
                  )}
                </p>
              </>
            )}
            
            {activePhaseData.id === 3 && (
              <>
                <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-6 leading-relaxed`}>
                  {activePhaseData.content.intro.split('**').map((part, index) => 
                    index % 2 === 1 ? (
                      <strong key={index}>{part}</strong>
                    ) : (
                      <span key={index}>{part}</span>
                    )
                  )}
                </p>
                <ul className="space-y-4 mb-6">
                  {activePhaseData.content.items.map((item, index) => (
                    <li key={index} className="flex items-start">
                      <Circle className={`w-5 h-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'} mr-3 flex-shrink-0 mt-0.5`} />
                      <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} leading-relaxed`}>
                  {activePhaseData.content.closing.split('**').map((part, index) => 
                    index % 2 === 1 ? (
                      <strong key={index}>{part}</strong>
                    ) : (
                      <span key={index}>{part}</span>
                    )
                  )}
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default RoadmapSection;
